import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const InteractiveFAQ = () => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [openItems, setOpenItems] = useState(new Set([1]));

    const faqData = useMemo(() => t('faq.items', { returnObjects: true }) || [], [t]);
    const categories = t('faq.categories', { returnObjects: true }) || [];

    const filteredFAQs = useMemo(() => {
        if (!Array.isArray(faqData)) return [];
        return faqData.filter(item => {
            const matchesSearch = item.question?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                item.answer?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
            return matchesSearch && matchesCategory;
        });
    }, [searchTerm, selectedCategory, faqData]);

    const toggleItem = (id) => {
        const newOpenItems = new Set(openItems);
        if (newOpenItems.has(id)) {
            newOpenItems.delete(id);
        } else {
            newOpenItems.add(id);
        }
        setOpenItems(newOpenItems);
    };

    const expandAll = () => {
        setOpenItems(new Set(filteredFAQs.map(item => item.id)));
    };

    const collapseAll = () => {
        setOpenItems(new Set());
    };

    return (
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <div className="text-center max-w-2xl mx-auto mb-14">
                <span className="text-xs font-semibold uppercase tracking-wider text-terracotta mb-2 block">
                    Întrebări Frecvente
                </span>
                <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 dark:text-stone-100 mb-4">
                    {t('faq.ui.title')}
                </h2>
                <p className="text-stone-600 dark:text-stone-400 text-base sm:text-lg">
                    {t('faq.ui.subtitle')}
                </p>
            </div>

            <div className="max-w-4xl mx-auto">
                {/* Search and filters */}
                <div className="mb-10 space-y-4">
                    {/* Search bar */}
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-stone-400">
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        <input
                            type="text"
                            placeholder={t('faq.ui.search_placeholder')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="block w-full pl-11 pr-4 py-3.5 rounded-full bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 text-stone-800 dark:text-stone-200 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-terracotta text-sm shadow-xs"
                        />
                    </div>

                    {/* Category filters */}
                    {Array.isArray(categories) && categories.length > 0 && (
                        <div className="flex flex-wrap gap-2 pt-1">
                            {categories.map(category => (
                                <button
                                    key={category.id}
                                    onClick={() => setSelectedCategory(category.id)}
                                    className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${
                                        selectedCategory === category.id
                                            ? 'bg-terracotta text-white shadow-soft'
                                            : 'bg-white dark:bg-stone-900 text-stone-600 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 border border-stone-200 dark:border-stone-800'
                                    }`}
                                >
                                    <span className="mr-1.5">{category.icon}</span>
                                    {category.name}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Expand/Collapse controls */}
                    <div className="flex justify-between items-center text-xs text-stone-500 dark:text-stone-400 pt-2 px-1">
                        <span>
                            {filteredFAQs.length} {filteredFAQs.length === 1 ? t('faq.ui.found_single') : t('faq.ui.found_plural')}
                        </span>
                        <div className="space-x-3">
                            <button
                                onClick={expandAll}
                                className="text-terracotta hover:underline font-semibold"
                            >
                                {t('faq.ui.expand_all')}
                            </button>
                            <span>•</span>
                            <button
                                onClick={collapseAll}
                                className="text-terracotta hover:underline font-semibold"
                            >
                                {t('faq.ui.collapse_all')}
                            </button>
                        </div>
                    </div>
                </div>

                {/* FAQ Items */}
                <div className="space-y-4">
                    {filteredFAQs.length === 0 ? (
                        <div className="text-center py-12 bg-white dark:bg-stone-900 rounded-3xl border border-stone-200/80 dark:border-stone-800">
                            <p className="text-stone-500 dark:text-stone-400 text-sm mb-3">{t('faq.ui.not_found_msg')}</p>
                            <button
                                onClick={() => {
                                    setSearchTerm('');
                                    setSelectedCategory('all');
                                }}
                                className="text-xs font-semibold text-terracotta hover:underline"
                            >
                                {t('faq.ui.reset_filters')}
                            </button>
                        </div>
                    ) : (
                        filteredFAQs.map((item) => (
                            <div
                                key={item.id}
                                className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200/80 dark:border-stone-800 shadow-soft overflow-hidden transition-all duration-200"
                            >
                                <button
                                    onClick={() => toggleItem(item.id)}
                                    className="w-full px-6 py-5 text-left flex justify-between items-center hover:bg-stone-50 dark:hover:bg-stone-800/50 transition-colors"
                                    aria-expanded={openItems.has(item.id)}
                                >
                                    <h3 className="font-display font-bold text-stone-900 dark:text-stone-100 text-base sm:text-lg pr-4">
                                        {item.question}
                                    </h3>
                                    <div className={`w-8 h-8 rounded-full bg-stone-100 dark:bg-stone-800 flex items-center justify-center flex-shrink-0 text-terracotta transition-transform duration-300 ${openItems.has(item.id) ? 'rotate-180 bg-terracotta-50' : ''}`}>
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M19 9l-7 7-7-7"
                                            />
                                        </svg>
                                    </div>
                                </button>
                                {openItems.has(item.id) && (
                                    <div className="px-6 pb-6 pt-2 border-t border-stone-100 dark:border-stone-800/80 text-stone-600 dark:text-stone-300 text-sm sm:text-base leading-relaxed">
                                        {item.answer}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>

                {/* Direct Contact reassurance */}
                <div className="mt-14 text-center bg-white dark:bg-stone-900 rounded-3xl p-8 sm:p-10 shadow-elevated border border-stone-200/80 dark:border-stone-800">
                    <h3 className="font-display text-2xl font-bold text-stone-900 dark:text-stone-100 mb-3">
                        {t('faq.ui.cta_title')}
                    </h3>
                    <p className="text-stone-600 dark:text-stone-400 text-sm sm:text-base max-w-md mx-auto mb-8 leading-relaxed">
                        {t('faq.ui.cta_desc')}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a
                            href="mailto:psihoterapeut@oanatenea.ro"
                            className="px-7 py-3 rounded-full bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 text-stone-800 dark:text-stone-200 font-semibold text-sm transition-colors"
                        >
                            {t('faq.ui.cta_btn1')}
                        </a>
                        <Link
                            to="/contact"
                            className="px-7 py-3 rounded-full bg-terracotta hover:bg-terracotta-dark text-white font-semibold text-sm shadow-warm transition-all duration-200 transform hover:-translate-y-0.5"
                        >
                            {t('faq.ui.cta_btn2')}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InteractiveFAQ;
